import React, { useContext, useState } from 'react';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { Company } from '../types';
import {
  StyledCard, CardTitle, FormGrid, StyledFormGroup,
  TooltipWrapper, InfoIcon, DropdownList, DropdownItem,
} from '../cardStyles';
import {
  HeaderRow,
  EditToggleButton,
  SaveCancelBar,
  SmallOutlineButton,
  SmallSolidButton,
  EditedNotice,
} from './DestinatarioSection.style';

interface Props {
  companies: Company[];
  selectedDestinatario: { company: Company } | null;
  onSelect: (value: { company: Company }) => void;
  readOnly?: boolean;
  /** nota já criada: a escolha é persistida no salvamento, como no frete */
  invoiceId?: number;
  onSaved?: () => void;
  /** cliente do pedido de origem — a divergência é o que marca a nota como personalizada */
  orderClientId?: number | null;
}

const LabelWithInfo = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

const getEnderecoFormatted = (company: Company | null | undefined): string => {
  if (!company) return '';
  const address = (company as any).Address || (company as any).address;
  if (!address) return '';

  const logradouro = address.street || '';
  const numero = address.number || '';
  const complemento = address.complement || '';
  const cepVal = address.cep
    ? String(address.cep).replace(/^(\d{5})(\d{3})$/, '$1-$2')
    : '';

  const neighborhood = address.Neighborhood || address.neighborhood;
  const city = neighborhood?.City || neighborhood?.city;
  const cityName = city?.name || '';
  const stateUf = city?.State?.uf || city?.state?.uf || '';

  const parts = [];
  if (logradouro) parts.push(logradouro);
  if (numero) parts.push(numero);
  if (complemento) parts.push(complemento);
  if (cepVal) parts.push(`CEP ${cepVal}`);

  const baseAddress = parts.join(', ');
  const cityState =
    cityName && stateUf ? `${cityName}/${stateUf}` : cityName || stateUf || '';

  if (baseAddress && cityState) return `${baseAddress} — ${cityState}`;
  return baseAddress || cityState;
};

const contribuinteLabel = (company: Company | null): string => {
  switch ((company as any)?.contribuinteIcms) {
    case 'CONTRIBUINTE':
      return 'Contribuinte';
    case 'ISENTO':
      return 'Isento';
    case 'NAO_CONTRIBUINTE':
      return 'Não contribuinte';
    default:
      return '—';
  }
};

/** Campos derivados do destinatário — os mesmos em leitura e em edição, só a fonte muda. */
const DadosDoDestinatario: React.FC<{ company: Company | null }> = ({ company }) => (
  <>
    <StyledFormGroup>
      <LabelWithInfo label="Razão Social" tooltip="Nome completo ou Razão Social do destinatário" />
      <input type="text" placeholder="Nome do cliente" value={company?.companyName || ''} readOnly />
    </StyledFormGroup>

    <StyledFormGroup>
      <LabelWithInfo label="Contribuinte ICMS" tooltip="Status de contribuinte do destinatário" />
      <input type="text" readOnly value={contribuinteLabel(company)} />
    </StyledFormGroup>

    <StyledFormGroup>
      <LabelWithInfo label="Inscrição Estadual" tooltip="Enviada apenas para destinatário contribuinte" />
      <input
        type="text"
        readOnly
        placeholder={(company as any)?.contribuinteIcms === 'CONTRIBUINTE' ? '' : 'Não aplicável'}
        value={
          (company as any)?.contribuinteIcms === 'CONTRIBUINTE'
            ? (company as any)?.stateRegistration || ''
            : ''
        }
      />
    </StyledFormGroup>

    <div style={{
      gridColumn: 'span 2',
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.7fr)',
      gap: '20px 30px',
      alignItems: 'start',
    }}>
      <StyledFormGroup>
        <LabelWithInfo label="E-mail" tooltip="E-mail para envio automático" />
        <input type="email" placeholder="nfe@cliente.com.br" value={(company as any)?.email || ''} readOnly />
      </StyledFormGroup>
      <StyledFormGroup>
        <LabelWithInfo label="Endereço" tooltip="Endereço completo do destinatário" />
        <input
          type="text"
          title={getEnderecoFormatted(company)}
          placeholder="Logradouro, número, complemento, CEP — Cidade/UF"
          value={getEnderecoFormatted(company)}
          readOnly
        />
      </StyledFormGroup>
    </div>
  </>
);

const DestinatarioSection: React.FC<Props> = ({
  companies,
  selectedDestinatario,
  onSelect,
  readOnly = false,
  invoiceId,
  onSaved,
  orderClientId,
}) => {
  const { setFeedback } = useContext(FeedbackContext);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // A escolha fica local até salvar: trocar o destinatário muda a UF de destino e com ela o
  // cenário fiscal dos itens, então a tela só reresolve imposto depois que a nota gravou.
  const [pendente, setPendente] = useState<Company | null>(null);

  const companyAtual = selectedDestinatario?.company ?? null;
  // Nota já criada tem o que salvar; na criação não existe nota e a escolha vive no pai.
  const podeSalvar = !readOnly && invoiceId != null && !!onSaved;
  const clienteProprio =
    companyAtual != null && orderClientId != null && companyAtual.id !== orderClientId;

  const filtered = companies.filter((c) => {
    const q = search.toLowerCase();
    return (
      String(c.cnpj || '').toLowerCase().includes(q) ||
      String(c.companyName || '').toLowerCase().includes(q)
    );
  });

  const beginEdit = () => {
    setPendente(companyAtual);
    setSearch('');
    setEditing(true);
  };

  const handleSalvar = async () => {
    if (!pendente || invoiceId == null) return;
    setSaving(true);
    try {
      await ApiService.fiscal.updateInvoice(invoiceId, { companyClientId: pendente.id });
      setEditing(false);
      setFeedback({ message: 'Destinatário da nota atualizado.', isOpen: true, statusOk: true });
      // Recarrega a nota: é dela que sai o destinatário e, por ele, a UF que reresolve o imposto.
      onSaved?.();
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      setFeedback({
        message: Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao salvar o destinatário da nota.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const buscaDestinatario = (
    escolhido: Company | null,
    aoEscolher: (company: Company) => void,
  ) => (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Pesquisar Destinatário..."
        autoComplete="off"
        value={isOpen ? search : escolhido?.cnpj || ''}
        onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
        onFocus={() => { setSearch(''); setIsOpen(true); }}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        style={{ background: '#fff', textAlign: 'left' }}
      />
      {isOpen && filtered.length > 0 && (
        <DropdownList style={{ top: 'calc(100% + 4px)', zIndex: 9999, borderRadius: '8px' }}>
          {filtered.map((c) => (
            <DropdownItem key={c.id} onMouseDown={() => { aoEscolher(c); setIsOpen(false); }}>
              <span style={{ fontSize: '12px', color: '#333' }}>{c.cnpj} — {c.companyName}</span>
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </div>
  );

  if (editing) {
    return (
      <StyledCard>
        <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase', marginBottom: '20px' }}>
          Destinatário
        </CardTitle>
        <FormGrid cols={2}>
          <StyledFormGroup>
            <LabelWithInfo label="CNPJ / CPF" tooltip="CNPJ (14 dígitos) ou CPF (11 dígitos) do destinatário" />
            {buscaDestinatario(pendente, setPendente)}
          </StyledFormGroup>
          <DadosDoDestinatario company={pendente} />
        </FormGrid>
        <SaveCancelBar>
          <SmallOutlineButton type="button" onClick={() => setEditing(false)} disabled={saving}>
            Cancelar
          </SmallOutlineButton>
          <SmallSolidButton type="button" onClick={handleSalvar} disabled={saving || !pendente}>
            {saving ? 'Salvando…' : 'Salvar destinatário'}
          </SmallSolidButton>
        </SaveCancelBar>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <HeaderRow>
        <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase', paddingBottom: 0 }}>
          Destinatário
        </CardTitle>
        {podeSalvar && <EditToggleButton onClick={beginEdit}>Editar destinatário</EditToggleButton>}
      </HeaderRow>

      {clienteProprio && (
        <EditedNotice>Destinatário personalizado nesta nota — diferente do pedido de origem.</EditedNotice>
      )}

      <FormGrid cols={2}>
        <StyledFormGroup>
          <LabelWithInfo label="CNPJ / CPF" tooltip="CNPJ (14 dígitos) ou CPF (11 dígitos) do destinatário" />
          {readOnly || podeSalvar ? (
            <input type="text" readOnly value={companyAtual?.cnpj || '—'} />
          ) : (
            // Criação: não existe nota para gravar, a escolha vive no estado do pai.
            buscaDestinatario(companyAtual, (c) => onSelect({ company: c }))
          )}
        </StyledFormGroup>
        <DadosDoDestinatario company={companyAtual} />
      </FormGrid>
    </StyledCard>
  );
};

export default DestinatarioSection;
